import { useEffect, useMemo, useState } from "react";
import { Loader2, Plus, Trash2, Upload, Eye, EyeOff } from "lucide-react";
import { Button, Modal, Field, Select, useToast } from "../../../shared/ui";
import {
  listSystemPresets,
  createSystemPreset,
  updateSystemPreset,
  deleteSystemPreset,
  type SystemPreset,
  type SystemPresetInput,
  type PresetKind,
} from "../../../features/admin/api/adminApi";
import { PageHeader, StatusPill, shortDate } from "./_shared";
import { LIBRARY_KINDS, KIND_LABEL, KIND_ACCEPT, assetKindFor, MAX_UPLOAD_BYTES, parseTags } from "./presetLibrary";

/**
 * The curated preset library — the surface that lets the catalogue grow without
 * a release. Before this page the only way to create a system preset was
 * scripts/seed-system-presets.ts.
 *
 * Scale we are building toward, measured from iJewel3D's live playground: 1,169
 * assets across 12 groups, every row carrying a file, a thumbnail, a name and
 * tags. Ours starts at the 30 static files the seed script imports.
 */

const readAsDataUrl = (file: File) =>
  new Promise<string>((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(String(fr.result));
    fr.onerror = () => reject(new Error(`Could not read ${file.name}`));
    fr.readAsDataURL(file);
  });

/** Admin-themed file picker. `DropZone` in src/ui/kit is styled with the editor's
 *  MD3 tokens (outline / primary), which do not belong in the Vault admin chrome —
 *  hence a local one on ink/gold rather than importing across design systems. */
function FilePick({ label, accept, file, onPick }: {
  label: string; accept: string; file: File | null; onPick: (f: File | null) => void;
}) {
  const pick = () => {
    const inp = document.createElement("input");
    inp.type = "file";
    inp.accept = accept;
    inp.onchange = () => onPick(inp.files?.[0] ?? null);
    inp.click();
  };
  return (
    <div>
      <p className="mb-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-ink-400">{label}</p>
      <button
        type="button"
        onClick={pick}
        className="flex w-full items-center gap-2 rounded-lg border border-dashed border-ink-200 px-3 py-3 text-left text-[12.5px] text-ink-500 transition-colors hover:border-gold-400 hover:text-ink-700"
      >
        <Upload size={14} />
        {file ? `${file.name} · ${(file.size / 1048576).toFixed(2)} MB` : `Choose a file (${accept})`}
      </button>
    </div>
  );
}

const blank = (): SystemPresetInput => ({ kind: "env", name: "", tier: "free", status: "draft", order: 0, tags: [] });

export default function AdminPresets() {
  const { toast } = useToast();
  const [rows, setRows] = useState<SystemPreset[] | null>(null);
  const [filter, setFilter] = useState<PresetKind | "">("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<SystemPresetInput>(blank());
  const [assetFile, setAssetFile] = useState<File | null>(null);
  const [thumbFile, setThumbFile] = useState<File | null>(null);
  const [tagText, setTagText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => listSystemPresets().then(setRows).catch(() => setRows([]));
  useEffect(() => { void load(); }, []);

  const shown = useMemo(
    () => (rows ?? []).filter((r) => !filter || r.kind === filter),
    [rows, filter],
  );
  const counts = useMemo(() => {
    const m: Record<string, number> = {};
    for (const r of rows ?? []) m[r.kind] = (m[r.kind] ?? 0) + 1;
    return m;
  }, [rows]);

  const reset = () => { setForm(blank()); setAssetFile(null); setThumbFile(null); setTagText(""); };

  const submit = async () => {
    if (!form.name.trim()) { toast({ tone: "warning", title: "Give the preset a name" }); return; }
    const over = [assetFile, thumbFile].find((f) => f && f.size > MAX_UPLOAD_BYTES);
    if (over) {
      toast({
        tone: "error",
        title: "That file is too large to upload here",
        description: `${over.name} is ${(over.size / 1048576).toFixed(1)} MB. The limit is 25 MB — files travel base64 inside the request body.`,
      });
      return;
    }
    setBusy(true);
    try {
      const body: SystemPresetInput = {
        ...form,
        name: form.name.trim(),
        tags: parseTags(tagText),
      };
      if (assetFile) {
        body.dataUrl = await readAsDataUrl(assetFile);
        body.assetKind = assetKindFor(assetFile.name);
      }
      if (thumbFile) body.thumbDataUrl = await readAsDataUrl(thumbFile);
      await createSystemPreset(body);
      toast({ tone: "success", title: `“${body.name}” created` });
      setOpen(false);
      reset();
      await load();
    } catch (e) {
      toast({ tone: "error", title: "Could not create the preset", description: e instanceof Error ? e.message : undefined });
    } finally {
      setBusy(false);
    }
  };

  const togglePublish = async (p: SystemPreset) => {
    try {
      await updateSystemPreset(p._id, { status: p.status === "published" ? "draft" : "published" });
      await load();
    } catch (e) {
      toast({ tone: "error", title: "Could not change the status", description: e instanceof Error ? e.message : undefined });
    }
  };

  const remove = async (p: SystemPreset) => {
    if (!confirm(`Delete “${p.name}”? Its uploaded file is removed too. This cannot be undone.`)) return;
    try {
      await deleteSystemPreset(p._id);
      toast({ tone: "success", title: "Preset deleted" });
      await load();
    } catch (e) {
      toast({ tone: "error", title: "Could not delete the preset", description: e instanceof Error ? e.message : undefined });
    }
  };

  return (
    <>
      <PageHeader
        title="Preset library"
        subtitle="Curated presets every workspace can pick from — environments, backgrounds, stages, materials."
        actions={<Button onClick={() => { reset(); setOpen(true); }}><Plus size={14} /> New preset</Button>}
      />

      <div className="px-4 py-4 sm:px-6">
        <div className="mb-4 flex flex-wrap gap-1.5">
          <button
            onClick={() => setFilter("")}
            className={`rounded-full px-3 py-1 text-[12px] font-medium transition-colors ${filter === "" ? "bg-ink-900 text-white" : "bg-ink-100 text-ink-600 hover:bg-ink-200"}`}
          >
            All {rows ? `(${rows.length})` : ""}
          </button>
          {LIBRARY_KINDS.map((k) => (
            <button
              key={k}
              onClick={() => setFilter(k)}
              className={`rounded-full px-3 py-1 text-[12px] font-medium transition-colors ${filter === k ? "bg-ink-900 text-white" : "bg-ink-100 text-ink-600 hover:bg-ink-200"}`}
            >
              {KIND_LABEL[k]} {counts[k] ? `(${counts[k]})` : ""}
            </button>
          ))}
        </div>

        {rows === null ? (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin text-ink-300" /></div>
        ) : shown.length === 0 ? (
          <div className="surface-card px-4 py-12 text-center">
            <p className="text-[13.5px] text-ink-500">
              {rows.length === 0
                ? "No presets yet. Create one above, or run scripts/seed-system-presets.ts to import the files already in public/."
                : "Nothing in this category yet."}
            </p>
          </div>
        ) : (
          <div className="surface-card overflow-x-auto">
            <table className="w-full min-w-[720px] text-left text-[13px]">
              <thead className="border-b border-ink-100 text-[11px] uppercase tracking-[0.1em] text-ink-400">
                <tr>
                  <th className="px-4 py-2.5 font-semibold">Preset</th>
                  <th className="px-4 py-2.5 font-semibold">Category</th>
                  <th className="px-4 py-2.5 font-semibold">Tier</th>
                  <th className="px-4 py-2.5 font-semibold">Order</th>
                  <th className="px-4 py-2.5 font-semibold">Status</th>
                  <th className="px-4 py-2.5 font-semibold">Added</th>
                  <th className="px-4 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {shown.map((p) => (
                  <tr key={p._id} className="border-b border-ink-50 last:border-0">
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2.5">
                        <span
                          className="h-8 w-8 shrink-0 rounded-md border border-ink-100 bg-ink-50 bg-cover bg-center"
                          style={p.thumbUrl ? { backgroundImage: `url("${p.thumbUrl}")` } : undefined}
                        />
                        <div className="min-w-0">
                          <p className="truncate font-medium text-ink-900">{p.name}</p>
                          {p.tags.length > 0 && <p className="truncate text-[11px] text-ink-400">{p.tags.join(" · ")}</p>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-ink-600">{KIND_LABEL[p.kind] ?? p.kind}</td>
                    <td className="px-4 py-2.5 text-ink-600 capitalize">{p.tier}</td>
                    <td className="px-4 py-2.5 tabular text-ink-500">{p.order}</td>
                    <td className="px-4 py-2.5"><StatusPill status={p.status} /></td>
                    <td className="px-4 py-2.5 text-ink-400">{shortDate(p.createdAt)}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => void togglePublish(p)}
                          title={p.status === "published" ? "Unpublish (hide from workspaces)" : "Publish to every workspace"}>
                          {p.status === "published" ? <EyeOff size={14} /> : <Eye size={14} />}
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => void remove(p)} title="Delete">
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="New preset"
        description="Uploads go to the platform bucket and become visible to every workspace once published."
        footer={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={() => void submit()} disabled={busy}>
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Create
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3">
          <Select
            label="Category"
            value={form.kind}
            options={LIBRARY_KINDS.map((k) => ({ value: k, label: KIND_LABEL[k] }))}
            onChange={(e) => { setForm((f) => ({ ...f, kind: e.target.value as PresetKind })); setAssetFile(null); }}
          />
          <Field label="Name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          <FilePick
            label="Asset file"
            accept={KIND_ACCEPT[form.kind] ?? "*"}
            file={assetFile}
            onPick={setAssetFile}
          />
          <FilePick label="Thumbnail (PNG/JPG)" accept=".png,.jpg,.jpeg,.webp" file={thumbFile} onPick={setThumbFile} />
          <p className="-mt-1 text-[11px] text-ink-400">
            An .hdr/.exr has no browser preview, so the library grid shows this thumbnail instead.
          </p>
          <Field label="Tags (comma separated)" value={tagText} onChange={(e) => setTagText(e.target.value)} />
          <div className="grid grid-cols-2 gap-3">
            <Select
              label="Tier"
              value={form.tier}
              options={[{ value: "free", label: "Free" }, { value: "premium", label: "Premium" }, { value: "business", label: "Business" }]}
              onChange={(e) => setForm((f) => ({ ...f, tier: e.target.value as SystemPresetInput["tier"] }))}
            />
            <Select
              label="Status"
              value={form.status}
              options={[{ value: "draft", label: "Draft" }, { value: "published", label: "Published" }]}
              onChange={(e) => setForm((f) => ({ ...f, status: e.target.value as SystemPresetInput["status"] }))}
            />
          </div>
          <Field
            label="Order"
            type="number"
            value={String(form.order ?? 0)}
            onChange={(e) => setForm((f) => ({ ...f, order: Number(e.target.value) || 0 }))}
          />
          <p className="text-[11px] text-ink-400">
            Tier is stored and filterable but not yet enforced — everything is visible regardless.
          </p>
        </div>
      </Modal>
    </>
  );
}
